import useIntervallo from '../hooks/useIntervallo';
import { useCallback, useEffect, useState } from 'react';
import client from '../api/client';

const roleLabel = role => role === 'editor' ? 'Può modificare' : 'Può leggere';
export default function CalendarSharing({ calendar, onChanged }) {
  const [invitations, setInvitations] = useState([]);
  const [shares, setShares] = useState({ grants: [], invitations: [] });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('reader');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const id = calendar?.id;
  const owner = calendar?.role === 'owner';
  const refresh = useCallback(async () => {
    const inbox = await client.get('/v1/calendar-invitations/');
    setInvitations(inbox.data);
    if (id && owner) setShares({ ...(await client.get(`/v1/calendars/${id}/shares/`)).data, calendar: id });
    else setShares({ grants: [], invitations: [] });
  }, [id, owner]);
  useEffect(() => {
    refresh().catch(() => setError('Impossibile caricare le condivisioni.'));
  }, [refresh]);
  useIntervallo(() => refresh().catch(() => {}), 30000);
  const perform = async (operation, success) => {
    setBusy(true); setError(''); setNotice('');
    try { await operation(); await refresh(); await onChanged(); setNotice(success); }
    catch (failure) { setError(failure.response?.data?.message || failure.message || 'Operazione non riuscita. Riprova.'); }
    finally { setBusy(false); }
  };
  const answer = (invite, action) => perform(async () => {
    const response = await client.post(`/v1/calendar-invitations/${invite.id}/`, { action });
    if (action === 'accept' && response.data.status !== 'accepted') throw new Error('Invito non più disponibile: potrebbe essere scaduto o revocato.');
  },
    action === 'accept' ? 'Calendario aggiunto ai Condivisi.' : 'Invito aggiornato.');
  const currentShares = shares.calendar === id ? shares : { grants: [], invitations: [] };
  return <section aria-label="Condivisioni familiari" className="bg-surface border rounded-xl p-4 space-y-3">
    {invitations.length > 0 && <div className="space-y-2">
      <h2 className="font-semibold">Inviti ricevuti</h2>
      {invitations.map(invite => <div key={invite.id} className="border rounded-lg p-3 flex flex-wrap items-center justify-between gap-3">
        <p><b>{invite.calendar_name}</b><br /><span className="text-sm text-muted">Da {invite.owner_email} · {roleLabel(invite.role)}</span></p>
        <div className="flex gap-3"><button disabled={busy} onClick={() => answer(invite, 'accept')} className="text-brand-700 font-medium">Accetta</button><button disabled={busy} onClick={() => answer(invite, 'decline')} className="text-muted">Rifiuta</button></div>
      </div>)}
    </div>}
    <details>
      <summary className="cursor-pointer font-medium">Condivisione familiare{calendar ? ` · ${calendar.name}` : ''}</summary>
      <p className="text-sm text-muted mt-3">Condividi un singolo calendario con utenti Wozzap già registrati. Gli inviti compaiono qui nell’app; non vengono inviate email o messaggi WhatsApp.</p>
      {!calendar && <p className="text-sm mt-3">Seleziona un calendario per gestire gli accessi.</p>}
      {calendar && !owner && <p className="text-sm mt-3">Condiviso da {calendar.owner_email}. {roleLabel(calendar.role)}. Solo il proprietario può invitare altre persone o cambiare i permessi.</p>}
      {owner && <>
        <form className="flex flex-wrap gap-3 items-end mt-4" onSubmit={event => {
          event.preventDefault(); perform(async () => { await client.post(`/v1/calendars/${id}/shares/`, { email, role }); setEmail(''); }, 'Invito disponibile nell’account del destinatario.');
        }}>
          <label className="flex-1 min-w-48 text-sm">Email dell’utente<input type="email" required value={email} onChange={event => setEmail(event.target.value)} className="block w-full border rounded p-2 mt-1" /></label>
          <label className="text-sm">Permesso<select value={role} onChange={event => setRole(event.target.value)} className="block border rounded p-2 mt-1"><option value="reader">Può leggere</option><option value="editor">Può modificare</option></select></label>
          <button disabled={busy} className="bg-brand-700 text-white rounded px-4 py-2 disabled:opacity-40">Crea invito</button>
        </form>
        <div className="mt-4 space-y-2">{currentShares.grants.map(grant => <div key={grant.user_id} className="flex flex-wrap items-center gap-3 text-sm border-t pt-3">
          <span className="flex-1">{grant.email}</span>
          <select aria-label={`Permesso di ${grant.email}`} value={grant.role} disabled={busy} onChange={event => perform(() => client.patch(`/v1/calendars/${id}/shares/${grant.user_id}/`, { role: event.target.value }), 'Permesso aggiornato.')} className="border rounded p-2"><option value="reader">Può leggere</option><option value="editor">Può modificare</option></select>
          <button disabled={busy} onClick={() => perform(() => client.delete(`/v1/calendars/${id}/shares/${grant.user_id}/`), 'Accesso revocato.')} className="text-danger">Revoca accesso</button>
        </div>)}</div>
        <div className="mt-3 space-y-2">{currentShares.invitations.map(invite => <div key={invite.id} className="flex flex-wrap justify-between gap-3 text-sm">
          <span>{invite.recipient_email} · {roleLabel(invite.role)} · {invite.status === 'expired' ? 'Scaduto' : 'In attesa di accettazione'}</span>
          {invite.status === 'pending' && <button disabled={busy} onClick={() => answer(invite, 'revoke')} className="text-danger">Revoca invito</button>}
        </div>)}</div>
      </>}
    </details>
    {error && <p role="alert" className="text-danger text-sm">{error}</p>}
    {notice && <p role="status" className="text-brand-700 text-sm">{notice}</p>}
  </section>;
}
